import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const ThemeContext = createContext(null);

function getSystemTheme() {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
}

function resolveTheme(pref) {
  return pref === "system" || !pref ? getSystemTheme() : pref;
}

export function ThemeProvider({ children }) {
  // pref can be "light" | "dark" | "system"
  const [pref, setPref] = useState(() => localStorage.getItem("theme") || "system");

  const applyTheme = useCallback((preference) => {
    const resolved = resolveTheme(preference);
    const root = document.documentElement;
    if (resolved === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
  }, []);

  useEffect(() => {
    applyTheme(pref);
    localStorage.setItem("theme", pref);
  }, [pref, applyTheme]);

  // React to OS changes while in system mode
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      if ((localStorage.getItem("theme") || "system") === "system") applyTheme("system");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [applyTheme]);

  const setTheme = useCallback(async (next, persistToServer = true) => {
    setPref(next);
    if (persistToServer) {
      try {
        await api.patch("/auth/theme", { theme: next });
      } catch (e) {
        // Not authenticated yet — local persistence still applies.
      }
    }
  }, []);

  // Called after login/me to sync the user's saved preference.
  const syncFromUser = useCallback((userTheme) => {
    if (userTheme && userTheme !== (localStorage.getItem("theme") || "system")) {
      setPref(userTheme);
    }
  }, []);

  const resolved = resolveTheme(pref);

  const toggle = useCallback(() => {
    setTheme(resolved === "dark" ? "light" : "dark");
  }, [resolved, setTheme]);

  return (
    <ThemeContext.Provider value={{ pref, resolved, setTheme, toggle, syncFromUser }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
